import { FC } from "react";
import { useRecoilValue } from "recoil";

import { CardNumericComparisonContentTemplate } from "@ryde/controls";
import {
  ComparingEnum,
  currencySignSelector,
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  INumericComparisonCardContent,
  INumericComparisonCardSettings,
} from "@ryde/shared";

import {
  IDashboardStatData,
  TMetricUnit,
} from "../../dynamic-dashboard.models";

interface IDashboardStatCardProps extends IDashboardStatData {
  /** Names the value, in place of the `dashboard.<CARD>.DESCRIPTION` a system card would use. */
  metricLabel?: string;
}

/**
 * Number card for a report-backed card. Renders through the very same template as
 * the ready-made number cards, so the two are indistinguishable — a report card
 * just supplies its metric's label where a system card would look up its own.
 *
 * No comparison subheader: that template is built around a named card's
 * "vs last <weekday>" copy, which a report metric has no equivalent for.
 */
const STAT_CARD_SETTINGS: INumericComparisonCardSettings = {
  maxDiffPercent: 0,
  predicate: () => ComparingEnum.NONE,
  dataHook: (obj: INumericComparisonCardContent) => obj,
  hasSubheader: false,
};

export const DashboardStatCard: FC<IDashboardStatCardProps> = ({
  value,
  unit,
  metricLabel,
}) => {
  const currencySign = useRecoilValue(currencySignSelector);
  const unitToExtension: Record<TMetricUnit, string> = {
    currency: currencySign,
    percent: "%",
  };
  const extension = unit ? unitToExtension[unit] : "";

  return (
    <CardNumericComparisonContentTemplate
      cardType={DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON}
      cardName={DashboardCardNameEnum.NONE}
      content={{ valueToday: value }}
      settings={STAT_CARD_SETTINGS}
      description={metricLabel}
      extension={extension}
    />
  );
};
